import api from 'api';
import {SendRouteDto} from 'dto/send-route.dto';
import useDrivers from 'hooks/useDrivers';
import useVehicles from 'pages/Settings/Vehicles/useVehicles';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useQuery} from 'react-query';
import {useHistory} from 'react-router-dom';
import {BlogResponse, ListResponse} from 'types';
import {Route} from 'pages/DeliveryPlanner/useDeliveryPlanner';

async function getMessageTemplates() {
  const {data} = await api.get<ListResponse<BlogResponse>>(`/support/blogs/`, {
    params: {content_type: 'template'},
  });
  return data?.results;
}

export type SendRouteProps = {
  isOpen: boolean;
  route: Route;
  onClose: () => void;
  onSubmit: (
    route: Route,
    sendMassMessage: boolean,
    message?: string
  ) => Promise<any>;
};
export const useSendRoute = ({
  isOpen,
  route,
  onClose,
  onSubmit,
}: SendRouteProps) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: {isSubmitting},
  } = useForm<SendRouteDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      driver: undefined,
      vehicle: undefined,
      send_preliminary_message: false,
    },
  });
  const {vehicles = [], isLoading: isLoadingVehicles} = useVehicles();
  const {drivers, isLoading: isLoadingDrivers} = useDrivers();
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const {push} = useHistory();
  const {data: messageTemplates} = useQuery(
    ['message_templates'],
    getMessageTemplates,
    {
      refetchOnWindowFocus: false,
      enabled: !!(isEditingMessage && watch('send_preliminary_message')),
    }
  );
  const vehiclesOptions = useMemo(() => {
    return vehicles.map((vehicle) => ({
      value: vehicle.id,
      label: vehicle.model,
    }));
  }, [vehicles]);

  const driversOptions = useMemo(() => {
    return drivers.map((driver) => ({
      value: driver.id,
      label: driver.full_name || driver.email,
    }));
  }, [drivers]);

  const templateOptions = useMemo(() => {
    return messageTemplates?.map((template) => ({
      value: template?.content,
      label: template.title,
    }));
  }, [messageTemplates]);

  const handleSubmitForm = useCallback(
    async (data: SendRouteDto) => {
      const driver = drivers.find((driver) => data.driver?.value === driver.id);
      const vehicle = vehicles.find(
        (vehicle) => data.vehicle?.value === vehicle.id
      );

      await onSubmit(
        {
          ...route,
          driver: driver || route.driver,
          vehicle: vehicle || route.vehicle,
        },
        data.send_preliminary_message,
        data.message
      );

      onClose();
    },
    [drivers, onClose, onSubmit, route, vehicles]
  );

  useEffect(() => {
    reset({
      driver: route.driver
        ? {
            value: route.driver.id,
            label:
              route.driver.full_name ||
              `${route.driver.first_name} ${route.driver.last_name}` ||
              '',
          }
        : undefined,
      vehicle: route.vehicle
        ? {
            value: route.vehicle.id,
            label: route.vehicle.nickname || route.vehicle.model,
          }
        : undefined,
    });
  }, [isOpen, reset, route, route.driver, route.vehicle]);

  return {
    control,
    handleSubmit,
    watch,
    setValue,
    isSubmitting,
    isOpen,
    isEditingMessage,
    setIsEditingMessage,
    vehiclesOptions,
    driversOptions,
    templateOptions,
    handleSubmitForm,
    isLoadingVehicles,
    isLoadingDrivers,
    push,
  };
};
